/**
 * Generates a printable HTML document (A4 RTL) for إذن صرف مواد طبية — وزارة الصحة.
 * Uses fields from GetMedicineOrder response (detail).
 */
import moment from 'moment';

function escapeHtml(s) {
    if (s == null || s === '') return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function fmtDate(v) {
    if (!v) return '—';
    try {
        return moment(v).format('YYYY/MM/DD HH:mm');
    } catch {
        return '—';
    }
}

export function buildDispenseAuthorizationHtml(detail, options) {
    const opts = options || {};
    const branchName = escapeHtml(detail.branchName);
    const code = escapeHtml(detail.code);
    const note = detail.note ? escapeHtml(detail.note) : '';
    const reviewNote = detail.reviewNote ? escapeHtml(detail.reviewNote) : '';
    const created = fmtDate(detail.createdOn);
    const reviewedOn = detail.reviewedOn ? fmtDate(detail.reviewedOn) : '—';
    const dispensedOn = detail.dispensedOn ? fmtDate(detail.dispensedOn) : '—';
    const createdByName = escapeHtml(detail.createdByName || '');
    const reviewedByName = escapeHtml(detail.reviewedByName || '');
    const dispensedByName = escapeHtml(detail.dispensedByName || '');

    const lines = detail.lines || detail.Lines || [];
    const tableRows = lines
        .map((ln, idx) => {
            const rowIndex = idx + 1;
            const approved = ln.quantityApproved != null ? Number(ln.quantityApproved) : null;
            const disp = Number(ln.quantityDispensed || 0);
            const req = Number(ln.quantityRequested || 0);
            const remaining =
                approved != null ? Math.max(0, approved - disp) : Math.max(0, req - disp);
            return `<tr class="${rowIndex % 2 === 0 ? 'bg-gray-striped-2' : 'bg-gray-striped-1'}">
        <td>${rowIndex}</td>
        <td>${escapeHtml(ln.productsId)}</td>
        <td class="text-right">${escapeHtml(ln.productName)}${ln.batchNumber ? ` <span class="text-muted">/ ${escapeHtml(ln.batchNumber)}</span>` : ''}</td>
        <td>${escapeHtml(ln.model || '—')}</td>
        <td>${escapeHtml(ln.material || '—')}</td>
        <td>—</td>
        <td>${req}</td>
        <td>${approved != null ? approved : '—'}</td>
        <td>${disp}</td>
        <td>${remaining}</td>
      </tr>`;
        })
        .join('');

    let totalDispensed = 0;
    lines.forEach((ln) => {
        totalDispensed += Number(ln.quantityDispensed || 0);
    });

    const printTitle = opts.title || 'إذن صرف مواد طبية';
    const docRef = code || String(detail.id || '');
    const logoUrl = opts.logoUrl || '';
    const logoBlock = logoUrl
        ? `<img src="${escapeHtml(logoUrl)}" alt="" class="moh-logo" width="120" height="auto" />`
        : '';

    return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(printTitle)} — ${docRef}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/css/bootstrap.min.css" rel="stylesheet" crossorigin="anonymous" />
  <style>
    body { width:100%; margin:0; padding:0; background:#fafafa; color:#1a1a1a; font:11px "Cairo",sans-serif; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    * { box-sizing:border-box; }
    .moh-header { border-bottom:3px solid #0d6b4d; padding-bottom:8px; margin-bottom:12px; }
    .moh-brand { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .moh-logo { height:52px; width:auto; max-width:140px; object-fit:contain; flex-shrink:0; }
    .moh-title { color:#0d6b4d; font-weight:700; font-size:18px; letter-spacing:0.02em; }
    .moh-sub { font-size:12px; color:#444; margin-top:4px; }
    .doc-title { font-size:15px; font-weight:700; margin:8px 0 4px; }
    .page-container { width:210mm; min-height:297mm; margin:10mm auto; border:1px solid #d3d3d3; border-radius:4px; background:#fff; box-shadow:0 0 5px rgba(0,0,0,.08); }
    .page-inner { padding:10mm; min-height:297mm; }
    @page { size:A4; margin:0; }
    @media print {
      html, body { width:210mm; height:297mm; background:#fff; }
      .page-container { margin:0; border:none; box-shadow:none; width:100%; min-height:auto; }
    }
    th, td { line-height:1.35; font-size:10px; vertical-align:middle !important; }
    .bg-gray { background-color:#b8c9c4 !important; color:#111; }
    .bg-gray-striped-1 { background-color:#eef4f2 !important; }
    .bg-gray-striped-2 { background-color:#f5f9f7 !important; }
    .print-footer { margin-top:16px; padding-top:10px; border-top:1px solid #ccc; font-size:9px; color:#555; line-height:1.6; }
    .signatures { margin-top:14px; }
    .signatures p { font-size:10px; line-height:1.5; }
  </style>
</head>
<body>
  <div class="page-container">
    <div class="page-inner">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div class="moh-header flex-grow-1">
          <div class="moh-brand">
            ${logoBlock}
            <div>
              <div class="moh-title">وزارة الصحة</div>
              <div class="moh-sub">المخزن المركزي — إدارة صرف الأدوية والمستلزمات الطبية للمراكز الصحية</div>
            </div>
          </div>
        </div>
        <div class="text-end" style="min-width:140px;">
          <div class="doc-title">${escapeHtml(printTitle)}</div>
          <div class="small text-muted">تاريخ الطباعة: ${escapeHtml(moment().format('YYYY/MM/DD HH:mm'))}</div>
        </div>
      </div>

      <div class="row gx-3">
        <div class="col-6">
          <table class="table table-sm table-bordered mb-2">
            <tbody>
              <tr class="bg-gray-striped-1"><th width="32%">الجهة المستلمة</th><td>${branchName}</td></tr>
              <tr class="bg-gray-striped-2"><th>رقم الطلب / العملية</th><td>${code}</td></tr>
              <tr class="bg-gray-striped-1"><th>ملاحظة المركز</th><td>${note || '—'}</td></tr>
              <tr class="bg-gray-striped-2"><th>ملاحظة المراجعة</th><td>${reviewNote || '—'}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="col-6">
          <table class="table table-sm table-bordered mb-2">
            <tbody>
              <tr class="bg-gray-striped-1"><th width="32%">تاريخ إنشاء الطلب</th><td>${created}</td></tr>
              <tr class="bg-gray-striped-2"><th>تاريخ المراجعة</th><td>${reviewedOn}</td></tr>
              <tr class="bg-gray-striped-1"><th>آخر صرف مسجل</th><td>${dispensedOn}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <table class="table table-sm table-bordered text-center border border-dark align-middle mt-1">
        <thead>
          <tr class="bg-gray">
            <th>#</th>
            <th width="72">رقم المادة</th>
            <th>البيان ورقم التشغيلة</th>
            <th>الطراز / الموديل</th>
            <th>التعبئة</th>
            <th>الصلاحية</th>
            <th>المطلوب</th>
            <th>المعتمد</th>
            <th>المصروف</th>
            <th>المتبقي</th>
          </tr>
        </thead>
        <tbody>${tableRows || '<tr><td colspan="10" class="text-center">لا توجد بنود</td></tr>'}</tbody>
      </table>

      <div class="row text-center mt-2">
        <div class="col-12 d-flex flex-wrap justify-content-center gap-4">
          <p class="mb-0">عدد البنود: <b>${lines.length}</b></p>
          <p class="mb-0">إجمالي الوحدات المصروفة (مجموع البنود): <b>${totalDispensed}</b></p>
        </div>
      </div>

      <div class="row signatures text-center">
        <div class="col-4">
          <p><strong>أوافق على المصروفات</strong><br />
          <b>الاسم:</b> ..............................................<br />
          <b>الصفة:</b> ..............................................<br />
          <b>التوقيع والختم:</b> ....................................</p>
        </div>
        <div class="col-4">
          <p><strong>مسؤول الصرف — المخزن المركزي</strong><br />
          <b>الاسم:</b> ..............................................<br />
          <b>الصفة:</b> ..............................................<br />
          <b>التوقيع والختم:</b> ....................................</p>
        </div>
        <div class="col-4">
          <p><strong>أقر باستلام البنود بالكميات أعلاه</strong><br />
          <b>الاسم:</b> ..............................................<br />
          <b>الصفة:</b> ..............................................<br />
          <b>التوقيع وتاريخ الاستلام:</b> ..........................</p>
        </div>
      </div>

      <div class="print-footer">
        <p class="mb-1"><strong>بطاقة المستند:</strong> وثيقة إدارية لإذن صرف مواد طبية صادرة عن ${escapeHtml(
        opts.buildFor || 'وزارة الصحة'
    )}؛ تُستخدم للطباعة والأرشفة وفق الإجراءات المعتمدة. لا يُعتد بالنسخ الناقصة التوقيع أو الختم.</p>
        <p class="mb-0 small">مرجع الطلب: <strong>${docRef}</strong>
        ${createdByName ? ` — إنشاء: ${createdByName}` : ''}
        ${reviewedByName ? ` — مراجعة: ${reviewedByName}` : ''}
        ${dispensedByName ? ` — صرف: ${dispensedByName}` : ''}</p>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function openPrintWindow(html) {
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) {
        return false;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
        try {
            w.print();
        } catch {
            /* ignore */
        }
    }, 300);
    return true;
}

export { escapeHtml };
